function waitForIceGathering(pc, timeoutMs = 5000) {
  if (pc.iceGatheringState === 'complete') return Promise.resolve();
  return new Promise((resolve) => {
    const timer = window.setTimeout(done, timeoutMs);
    function done() {
      window.clearTimeout(timer);
      pc.removeEventListener('icegatheringstatechange', check);
      resolve();
    }
    function check() {
      if (pc.iceGatheringState === 'complete') done();
    }
    pc.addEventListener('icegatheringstatechange', check);
  });
}

async function configureVideoSender(sender, videoEncoding = {}) {
  const parameters = sender.getParameters();
  if (!parameters.encodings?.length) return;

  parameters.degradationPreference = videoEncoding.degradationPreference || 'maintain-resolution';
  parameters.encodings[0] = {
    ...parameters.encodings[0],
    maxBitrate: videoEncoding.maxBitrate ?? 8_000_000,
    maxFramerate: videoEncoding.maxFramerate ?? 30,
    scaleResolutionDownBy: videoEncoding.scaleResolutionDownBy ?? 1,
  };

  await sender.setParameters(parameters);
}

function startAdaptiveVideoSender(sender, videoEncoding = {}) {
  const profiles = Array.isArray(videoEncoding.adaptiveProfiles) ? videoEncoding.adaptiveProfiles : [];
  if (!sender || profiles.length < 2 || typeof sender.getStats !== 'function') return { stop() {}, current: () => videoEncoding };

  let profileIndex = Math.min(profiles.length - 1, Math.max(0, Number(videoEncoding.initialProfile || 0)));
  let unhealthySamples = 0;
  let healthySamples = 0;
  let stopped = false;
  let sampling = false;

  const applyProfile = async (nextIndex) => {
    const boundedIndex = Math.min(profiles.length - 1, Math.max(0, nextIndex));
    if (boundedIndex === profileIndex) return;
    profileIndex = boundedIndex;
    await configureVideoSender(sender, { ...videoEncoding, ...profiles[profileIndex] });
  };

  const sample = async () => {
    if (stopped || sampling) return;
    sampling = true;
    try {
      const report = await sender.getStats();
      let outbound = null;
      let remoteInbound = null;
      let candidatePair = null;
      report.forEach((entry) => {
        if (entry.type === 'outbound-rtp' && !entry.isRemote && (entry.kind === 'video' || entry.mediaType === 'video')) outbound = entry;
        if (entry.type === 'remote-inbound-rtp' && (entry.kind === 'video' || entry.mediaType === 'video')) remoteInbound = entry;
        if (entry.type === 'candidate-pair' && entry.state === 'succeeded' && (entry.nominated || entry.selected)) candidatePair = entry;
      });
      if (!outbound) return;

      const activeProfile = profiles[profileIndex];
      const loss = Number(remoteInbound?.fractionLost);
      const roundTripTime = Number(remoteInbound?.roundTripTime ?? candidatePair?.currentRoundTripTime);
      const availableBitrate = Number(candidatePair?.availableOutgoingBitrate);
      const bandwidthLimited = outbound.qualityLimitationReason === 'bandwidth';
      const constrained = bandwidthLimited
        || (Number.isFinite(loss) && loss >= 0.055)
        || (Number.isFinite(roundTripTime) && roundTripTime >= 0.42)
        || (Number.isFinite(availableBitrate) && availableBitrate > 0 && availableBitrate < activeProfile.maxBitrate * 1.12);
      const healthy = Number.isFinite(availableBitrate) && availableBitrate > activeProfile.maxBitrate * 1.55
        && !bandwidthLimited
        && (!Number.isFinite(loss) || loss < 0.02)
        && (!Number.isFinite(roundTripTime) || roundTripTime < 0.24);

      unhealthySamples = constrained ? unhealthySamples + 1 : 0;
      healthySamples = healthy ? healthySamples + 1 : 0;
      if (unhealthySamples >= 2 && profileIndex < profiles.length - 1) {
        unhealthySamples = 0;
        healthySamples = 0;
        await applyProfile(profileIndex + 1);
      } else if (healthySamples >= 6 && profileIndex > 0) {
        unhealthySamples = 0;
        healthySamples = 0;
        await applyProfile(profileIndex - 1);
      }
    } catch (_error) {
      // Browser stats support varies; native WebRTC congestion control remains active.
    } finally {
      sampling = false;
    }
  };

  const timer = window.setInterval(sample, Math.max(2000, Number(videoEncoding.adaptationIntervalMs || 3000)));
  return {
    stop() { stopped = true; window.clearInterval(timer); },
    current() { return profiles[profileIndex]; },
  };
}

export async function createWhipPublisher({ stream, whipUrl, token, iceServers = [], onStateChange, videoEncoding = {} }) {
  if (!stream?.getTracks?.().length) throw new Error('Camera and microphone are not available');
  if (!whipUrl || !token) throw new Error('The Webinar media service is not configured');
  const pc = new RTCPeerConnection({ iceServers });
  const videoSenders = [];
  stream.getTracks().forEach((track) => {
    const sender = pc.addTrack(track, stream);
    if (track.kind === 'video') {
      track.contentHint = track.contentHint || 'motion';
      videoSenders.push(sender);
    }
  });
  pc.addEventListener('connectionstatechange', () => onStateChange?.(pc.connectionState));
  await pc.setLocalDescription(await pc.createOffer());
  await waitForIceGathering(pc);
  const response = await fetch(whipUrl, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/sdp',
    },
    body: pc.localDescription.sdp,
  });
  if (!response.ok) throw new Error(`Media connection failed (${response.status})`);
  await pc.setRemoteDescription({ type: 'answer', sdp: await response.text() });
  await Promise.all(videoSenders.map((sender) => configureVideoSender(sender, videoEncoding).catch(() => {})));
  const videoAdapters = videoSenders.map((sender) => startAdaptiveVideoSender(sender, videoEncoding));
  const location = response.headers.get('location');
  const resourceUrl = location ? new URL(location, whipUrl).toString() : '';
  return {
    pc,
    async replaceVideoTrack(track) {
      const sender = videoSenders[0];
      if (!sender) throw new Error('A replacement video sender is not available');
      await sender.replaceTrack(track);
      if (track) {
        track.contentHint = track.contentHint || 'motion';
        const activeEncoding = videoAdapters[0]?.current?.() || videoEncoding;
        await configureVideoSender(sender, { ...videoEncoding, ...activeEncoding }).catch(() => {});
      }
    },
    async close() {
      videoAdapters.forEach((adapter) => adapter.stop());
      if (resourceUrl) {
        await fetch(resourceUrl, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
      }
      pc.close();
    },
  };
}
